import {
  createBillingConfig,
  type Entitlements,
  MeterKey,
  PriceInterval,
  PriceModel,
  PriceType
} from './schema';


/**
 * Defines the user-facing names for all features available in KithFlow.
 * This enum is the single source of truth for what can be offered in a plan.
 */
enum Feature {
  // Core data layer
  UKCompanySearch = 'Unlimited UK Companies House search (5M+)',
  ProspectEnrichment = 'Prospect enrichment (website + verified email)',
  SourcedDossiers = 'Sourced battlecards & dossiers',
  IntentEngine = 'Intent Engine (scheduled discovery)',

  // Platform & integrations
  AICopilot = 'AI copilot (chat)',
  McpApiAccess = 'MCP & REST API access',
  IntegratedLeadManagement = 'Integrated lead management',

  // Higher-tier
  MultiClientWorkspace = 'Multi-client workspace',
  PrioritySupport = 'Priority support'
}

// Customer-facing plan prices are in GBP (UK-only). Note: provider COGS and the
// per-tier `costCapUsd` margin guard remain in USD (that's what we pay vendors).
const currency = 'GBP';

// ---------------------------------------------------------------------------
// STRIPE PRICE IDS — live GBP prices (Starter £49/£490 · Pro £149/£1,490 ·
// Agency £399/£3,990). Free has no Stripe object (placebo ids; never checks out).
// When you add/replace a tier's price in Stripe, paste the real `price_...` id on
// the matching plan below, then run:
//   pnpm --filter @workspace/billing generate:entitlements
// (entitlements.json is keyed by price id, so the engine needs the regen).
// ---------------------------------------------------------------------------

/**
 * Entitlements applied to an organisation with no active subscription — i.e. the
 * Free tier. Search is always free (never metered); enrichment gets a small
 * allowance so a new org can explore. Keep in sync with the `free` product below.
 */
export const DEFAULT_ENTITLEMENTS: Entitlements = {
  allowances: {
    [MeterKey.Enrichment]: 25,
    [MeterKey.Dossier]: 0,
    [MeterKey.Discovery]: 50
  },
  mcpEnabled: false,
  costCapUsd: 5
};


export const billingConfig = createBillingConfig({
  products: [
    {
      // The Free tier — top-of-funnel, no Stripe checkout. `id: 'free'` matches the
      // hardcoded currentProductId in the dashboard plan-selection, so its button
      // stays disabled and the placebo price ids never reach Stripe.
      id: 'free',
      name: 'Free',
      description: 'Explore the full UK register and try enrichment, free.',
      label: 'Start free',
      isFree: true,
      features: [
        Feature.UKCompanySearch,
        '25 enrichments / month',
        Feature.AICopilot
      ],
      entitlements: DEFAULT_ENTITLEMENTS,
      plans: [
        {
          id: 'plan-free-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id: 'price-free-monthly',
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              interval: PriceInterval.Month,
              cost: 0,
              currency
            }
          ]
        },
        {
          id: 'plan-free-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id: 'price-free-yearly',
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              interval: PriceInterval.Year,
              cost: 0,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'starter',
      name: 'Starter',
      description: 'For solo operators running outreach for a UK client.',
      label: 'Choose Starter',
      features: [
        Feature.UKCompanySearch,
        '100 enrichments / month',
        '200 discovery results / month',
        Feature.AICopilot,
        Feature.IntegratedLeadManagement
      ],
      entitlements: {
        allowances: {
          [MeterKey.Enrichment]: 100,
          [MeterKey.Dossier]: 0,
          [MeterKey.Discovery]: 200
        },
        mcpEnabled: false,
        // ~£20 COGS ceiling on the £49 plan — protects margin (USD: vendor cost).
        costCapUsd: 25
      },
      plans: [
        {
          id: 'plan-starter-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id: 'price_1Tfh7z2Z0Vm1AZC0wt2ZxrdR',
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              interval: PriceInterval.Month,
              cost: 49,
              currency
            }
          ]
        },
        {
          id: 'plan-starter-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id: 'price_1Tfh8N2Z0Vm1AZC01eSBH7Gk',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 490,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'For agencies and automation builders — dossiers + full API.',
      label: 'Choose Pro',
      recommended: true,
      features: [
        'Everything in Starter, plus:',
        '1,000 enrichments / month',
        '2,000 discovery results / month',
        '100 sourced dossiers / month',
        Feature.IntentEngine,
        Feature.McpApiAccess,
        '3 seats'
      ],
      entitlements: {
        allowances: {
          [MeterKey.Enrichment]: 1000,
          [MeterKey.Dossier]: 100,
          [MeterKey.Discovery]: 2000
        },
        mcpEnabled: true,
        seats: 3,
        costCapUsd: 75
      },
      plans: [
        {
          id: 'plan-pro-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id: 'price_1S2fMR2Z0Vm1AZC0lHPtwO5g',
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              interval: PriceInterval.Month,
              cost: 149,
              currency
            }
          ]
        },
        {
          id: 'plan-pro-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id: 'price_1S2fPb2Z0Vm1AZC0NRzIpN4p',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 1490,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'agency',
      name: 'Agency',
      description: 'High volume across multiple clients, with seats and priority support.',
      label: 'Choose Agency',
      features: [
        'Everything in Pro, plus:',
        '5,000 enrichments / month',
        '10,000 discovery results / month',
        '1,000 sourced dossiers / month',
        Feature.MultiClientWorkspace,
        '5 seats',
        Feature.PrioritySupport
      ],
      entitlements: {
        allowances: {
          [MeterKey.Enrichment]: 5000,
          [MeterKey.Dossier]: 1000,
          [MeterKey.Discovery]: 10000
        },
        mcpEnabled: true,
        seats: 5,
        costCapUsd: 250
      },
      plans: [
        {
          id: 'plan-agency-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id: 'price_1Tfh8h2Z0Vm1AZC0heeEchR0',
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              interval: PriceInterval.Month,
              cost: 399,
              currency
            }
          ]
        },
        {
          id: 'plan-agency-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id: 'price_1Tfh9A2Z0Vm1AZC0rFcKLBxg',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 3990,
              currency
            }
          ]
        }
      ]
    }
  ]
});

export const billingConfigDisplayIntervals = Array.from(
  new Set(
    billingConfig.products
      .filter((product) => !product.hidden)
      .flatMap((product) =>
        product.plans.flatMap((plan) => plan.displayIntervals)
      )
      .filter(Boolean)
  )
) as PriceInterval[];

// ---------------------------------------------------------------------------
// Pure entitlement helpers (no DB) — shared by the runtime resolver
// (entitlements.ts) and the generator (scripts/generate-entitlements.ts) so the
// generator never needs a database connection.
// ---------------------------------------------------------------------------

/** Merge a product's partial entitlements over the free default. */
export function withDefaultEntitlements(partial?: Entitlements): Entitlements {
  if (!partial) {
    return DEFAULT_ENTITLEMENTS;
  }
  return {
    allowances: { ...DEFAULT_ENTITLEMENTS.allowances, ...partial.allowances },
    mcpEnabled: partial.mcpEnabled ?? DEFAULT_ENTITLEMENTS.mcpEnabled,
    seats: partial.seats ?? DEFAULT_ENTITLEMENTS.seats,
    // Preserve an explicit null (uncapped); only fall back when undefined.
    costCapUsd:
      partial.costCapUsd !== undefined
        ? partial.costCapUsd
        : DEFAULT_ENTITLEMENTS.costCapUsd
  };
}

/** Entitlements for the product that owns a given Stripe price id (variantId). */
export function resolveEntitlementsForPriceId(
  priceId: string
): Entitlements | null {
  for (const product of billingConfig.products) {
    for (const plan of product.plans) {
      for (const price of plan.prices) {
        if (price.id === priceId) {
          return withDefaultEntitlements(product.entitlements);
        }
      }
    }
  }
  return null;
}
